export class SocketIOHandler {
  constructor(options) {
    this.options = options
    this.appClientWs = null
    this.trip = null
    this.weather = null
    // Set de uniqueIds que el cliente quiere observar via subscribecad.
    // Se mantiene aunque el socket aun no este conectado: al connect,
    // los emitimos. Al reconnect (drops), idem — el server pierde su
    // estado de subscriptions con el socket viejo.
    this._cadSubscriptions = new Set()
  }

  connect() {
    this._connectAppClient()
    this._connectTrip()
    this._connectWeather()
  }

  disconnect() {
    if (this.appClientWs) { this.appClientWs.disconnect(); this.appClientWs = null }
    if (this.trip) { this.trip.disconnect(); this.trip = null }
    if (this.weather) { this.weather.disconnect(); this.weather = null }
  }

  // API publica: pide al server que mande positions historicas + en vivo
  // del reportante asociado a este incidente (paridad con legacy
  // Map.vue:1376 trip.emit('subscribecad', uniqueId)). Llamar por cada
  // incident.uniqueId apenas se ingiere; idempotente — el set deduplica
  // y no re-emitimos si ya estaba subscripto en esta conexion.
  subscribeCad(uniqueId) {
    if (!uniqueId) return
    if (this._cadSubscriptions.has(uniqueId)) return
    this._cadSubscriptions.add(uniqueId)
    if (this.trip && this.trip.connected) {
      this.trip.emit('subscribecad', uniqueId)
    }
  }

  unsubscribeCad(uniqueId) {
    if (!uniqueId) return
    this._cadSubscriptions.delete(uniqueId)
    if (this.trip && this.trip.connected) {
      this.trip.emit('unsubscribecad', uniqueId)
    }
  }

  _connectAppClient() {
    var opts = this.options
    if (!opts.user || !opts.user.attributes || !opts.user.attributes.permission_sinaloaSOS) return
    if (!window.io) { console.warn('[socketio] window.io not available'); return }

    this.appClientWs = window.io('/', {
      path: '/ws/appclient',
      query: { token: opts.user.token, type: 'oper' },
    })

    var self = this
    this.appClientWs.on('active_calls', function(data) {
      if (Array.isArray(data)) {
        data.forEach(function(item) { opts.onSOSCall(item, false) })
        if (opts.onActiveCallsLoaded) opts.onActiveCallsLoaded(data)
      }
    })
    this.appClientWs.on('new_call', function(data) { opts.onSOSCall(data, true) })
    this.appClientWs.on('call_update', function(data) { opts.onSOSCall(data, false) })
    this.appClientWs.on('set_position', function(data) {
      // El server no siempre manda callId — paridad con Map.vue legacy,
      // que asume que set_position aplica a la llamada activa del operador.
      // Pasamos data.callId si existe (puede ser null); MapV2.onSOSPosition
      // resuelve el fallback desde store.map.sosActiveCall.
      if (!data || data.latitude == null || data.longitude == null) return
      opts.onSOSPosition(data.callId || null, [data.longitude, data.latitude])
    })
    this.appClientWs.on('end_call', function(data) {
      // El server puede enviar un string plano (el ID) o un objeto { callId, id }
      var callId = typeof data === 'string' ? data : (data && (data.callId || data.id || data._id))
      opts.onSOSEnd(callId)
    })
    this.appClientWs.on('leave_call', function() { opts.onSOSEnd(null) })
  }

  _connectTrip() {
    var opts = this.options
    if (!window.io) return

    this.trip = window.io('/', {
      path: '/api/incidents/locations/socket.io',
    })

    var self = this
    this.trip.on('connect', function() {
      var systems = ['RAPID-SOS', 'SIS-MUJERES', 'CANACAR', 'HANDHELD', 'ANERPV', 'MOTUM', 'CRUZROJA']
      systems.forEach(function(sys) { self.trip.emit('subscribesystem', sys) })
      // Re-emitir todos los subscribecad pendientes/activos. Al reconnect
      // el server pierde el estado del socket anterior; sin esto, dejaria
      // de mandar positions historicas de los incidents que ya teniamos.
      self._cadSubscriptions.forEach(function(uniqueId) {
        self.trip.emit('subscribecad', uniqueId)
      })
    })

    this.trip.on('subscribed', function(data) {
      if (Array.isArray(data)) data.forEach(function(item) { self._processTripData(item) })
    })
    this.trip.on('update', function(data) { self._processTripData(data) })
  }

  _processTripData(data) {
    var opts = this.options

    if (data.systemName === 'RAPID-SOS' || data.systemName === 'SIS-MUJERES') {
      // El payload viene envuelto:
      //   { systemName, cadId, systemId, additionalData, persons: [ { positions, lastPosition, ... } ] }
      // Tambien notificamos al MapV2 con el payload completo (cadId+systemId+
      // persons) para que enriquezca el incidente correspondiente con
      // rapidSosLocations / sisPersons — necesario para dibujar el trail
      // historico del GPS del reportante (paridad legacy IncidentStore.
      // setAdditionalData → writeMovement). El reportante en si solo se
      // ingiere para RAPID-SOS (legacy: SIS-MUJERES no genera marker
      // independiente, solo enriquece el incidente).
      if (typeof opts.onTripAdditionalData === 'function') {
        opts.onTripAdditionalData(data)
      }
      if (data.systemName === 'RAPID-SOS') {
        if (!Array.isArray(data.persons) || !data.persons[0]) return
        var person = data.persons[0]
        if (!Array.isArray(person.positions) || person.positions.length === 0) return
        person.cadId = data.cadId
        person.additionalData = Object.assign({}, data.additionalData || {}, person.additionalData || {})
        opts.onReportant(person)
      }
    } else {
      var item = Object.assign({}, data)
      if (data.additionalData) {
        item.latitude = item.latitude || data.additionalData.latitude
        item.longitude = item.longitude || data.additionalData.longitude
        item.name = item.name || data.additionalData.name || data.additionalData.plate
      }
      opts.onExternalSensor(item)
    }
  }

  _connectWeather() {
    var opts = this.options
    if (!window.io) return

    this.weather = window.io('/', {
      path: '/api/v2/ws/socket.io',
    })

    this.weather.on('message', function(messageData) {
      // Radio events (MCX / Tetra) tienen otro shape — no son change-stream
      // de MongoDB. Vienen como { model:'radio.call', type, data } o
      // { model:'radio.group', data }. Los enrutamos a callbacks dedicados
      // antes del switch genérico para no chocar con el cálculo de
      // updateDescription/fullDocument que aplica a las entidades de
      // weather/analytics.
      if (messageData.model === 'radio.call') {
        if (opts.onRadioCall) opts.onRadioCall(messageData)
        return
      }
      if (messageData.model === 'radio.group') {
        if (opts.onRadioGroup) opts.onRadioGroup(messageData.data)
        return
      }

      var data = (messageData.updateDescription && messageData.updateDescription.updatedFields)
        || messageData.fullDocument || messageData

      switch (messageData.model) {
        case 'Dam':
          opts.onWeatherEntity('presa', data)
          break
        case 'Sismo':
          opts.onWeatherEntity('sismo', data)
          break
        case 'Fire':
          opts.onWeatherEntity('fire', data)
          break
        case 'AnalyticRecord':
          if (opts.onHits) opts.onHits([data], 'AnalyticRecord')
          break
        case 'AnalyticExternalRecord':
          if (opts.onHits) opts.onHits([data], 'AnalyticExternalRecord')
          break
      }
    })

    // Disconnect/reconnect del socket weather afecta a las llamadas
    // radio en curso (el evento 'terminated' del backend MCX llega por
    // este socket). Si la conexión se cae durante una llamada activa,
    // el panel necesita saberlo para no mostrar "Conectado" estancado
    // y para refrescar el catálogo de groups al reconectar.
    this.weather.on('disconnect', function() {
      if (opts.onRadioWsDisconnect) opts.onRadioWsDisconnect()
    })
    this.weather.on('connect', function() {
      if (opts.onRadioWsReconnect) opts.onRadioWsReconnect()
    })
  }
}
